"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { motion } from "framer-motion";
import { PortableText, stegaClean } from "next-sanity";
import IQCLogo from "@/components/global/ui/IQCLogo";
import { IFinalCta, IFooter } from "@/sanity/lib/definitions";
import { resolvePageLink } from "@/sanity/globals/CTA";
import {
  resolveI18nText,
  resolveI18nRichtext,
  NON_DEFAULT_LANGUAGES,
  DEFAULT_LANGUAGE,
} from "@/sanity/globals/I18n";
import { richTextComponent } from "@/sanity/sanityPortableText";
import FooterAskAi from "./FooterAskAi";
import FooterCta from "./FooterCta";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20, filter: "blur(4px)" },
  whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const, delay },
});

export const RIGHTS_RESERVED: Record<string, string> = {
  en: "All rights reserved.",
  de: "Alle Rechte vorbehalten.",
  br: "Todos os direitos reservados.",
};

const DISCLAIMER_LABEL: Record<string, string> = {
  en: "Disclaimer:",
  de: "Haftungsausschluss:",
  br: "Aviso legal:",
};

export default function Footer({
  data,
  finalCta,
  hideFinalCta,
  showTrustpilot,
  wideFinalCtaTitle,
  language: pageLanguage,
}: {
  data?: IFooter;
  finalCta?: IFinalCta | null;
  hideFinalCta?: boolean;
  showTrustpilot?: boolean;
  wideFinalCtaTitle?: boolean;
  language?: string;
}) {
  const [loaded, setLoaded] = useState(false);
  const pathname = usePathname();
  const language =
    stegaClean(pageLanguage) ||
    (NON_DEFAULT_LANGUAGES.find(
      (l) => pathname.startsWith(`/${l}/`) || pathname === `/${l}`,
    ) ??
      DEFAULT_LANGUAGE);

  useEffect(() => {
    if (loaded) return;
    setLoaded(true);

    return () => {
      setLoaded(false);
    };
  }, []);

  const cta: IFinalCta | undefined = hideFinalCta
    ? undefined
    : finalCta?.finalCtaTitle || finalCta?.finalCtaImage
      ? finalCta
      : data && {
          finalCtaChip:
            resolveI18nText(data.finalCtaChip, data.finalCtaChipI18n, language) ||
            undefined,
          finalCtaTitle: resolveI18nRichtext(
            data.finalCtaTitle,
            data.finalCtaTitleI18n,
            language,
          ),
          finalCtaButton: data.finalCtaButton,
          finalCtaImage: data.finalCtaImage,
        };

  // Kept below the hooks: an early return above them changes the hook count
  // between renders as soon as `data` arrives.
  if (!data && !cta) return null;

  const disclaimer =
    data && resolveI18nRichtext(data.disclaimer, data.disclaimerI18n, language);

  return (
    <footer className="relative z-10 w-full text-white [text-wrap:wrap]">
      {cta && (
        <FooterCta
          data={cta}
          language={language}
          showTrustpilot={showTrustpilot}
          wideTitle={wideFinalCtaTitle}
        />
      )}

      {/* Prevent rendering on server side to avoid hydration mismatch */}
      {data && loaded && (
        <div className="relative w-full overflow-clip bg-[#001b13]">
          <div
            aria-hidden
            className="pointer-events-none absolute left-1/2 h-[50rem] w-[214rem] -translate-x-1/2 rounded-[50%] bg-[#0e4132] blur-[21.24rem] max-lg:bottom-[-87rem] lg:bottom-[-64rem]"
          />

          <div className="relative mx-auto flex flex-col max-lg:w-full max-lg:gap-[4rem] max-lg:px-[2.5rem] max-lg:py-[6rem] lg:w-[176.5rem] lg:gap-[5rem] lg:py-[9.875rem]">
            <motion.div
              {...fadeUp(0)}
              className="flex max-lg:flex-col max-lg:items-start max-lg:gap-[2rem] lg:h-[7.125rem] lg:flex-row lg:items-center lg:justify-between"
            >
              <IQCLogo className="max-lg:h-[4.23rem] max-lg:w-[22.125rem] lg:h-[4.636rem] lg:w-[24.25rem]" />
              <FooterAskAi data={data} language={language} />
            </motion.div>

            <div className="flex flex-col max-lg:gap-[3rem] lg:gap-[5rem]">
              <div className="flex max-lg:flex-wrap max-lg:gap-x-[3rem] max-lg:gap-y-[5rem] lg:w-[171rem] lg:justify-between">
                {(data.navColumns ?? []).map((col, i) => (
                  <motion.div
                    key={i}
                    {...fadeUp(0.1 + i * 0.05)}
                    className="flex flex-col gap-[1.5rem] max-lg:min-w-[21rem] max-lg:flex-[1_0_0] lg:min-w-[12.5rem]"
                  >
                    <span className="font-geist text-[1.75rem] leading-none uppercase text-white/[0.45]">
                      {resolveI18nText(col.heading, col.i18n, language)}
                    </span>
                    <div className="flex w-full flex-col items-start gap-[1.5rem]">
                      {col.links?.map((item, j) => {
                        const href =
                          resolvePageLink(item.link, language) ?? "#";
                        const isExternal =
                          item.link?.externalLink && item.link?.openInNewTab;
                        const isActive = !isExternal && href === pathname;

                        return (
                          <Link
                            key={j}
                            href={href}
                            target={isExternal ? "_blank" : "_self"}
                            rel={isExternal ? "noopener noreferrer" : undefined}
                            className={`flex max-w-full items-center gap-[1rem] font-geist text-[2rem] leading-none decoration-from-font [text-decoration-skip-ink:none] [text-underline-position:from-font] hover:underline ${
                              isActive ? "text-[#aef3e3]" : "text-white/80"
                            }`}
                          >
                            {isActive && (
                              <span
                                aria-hidden
                                className="h-[0.5rem] w-[0.5rem] shrink-0 bg-[#aef3e3]"
                              />
                            )}
                            <span className="max-lg:-my-[0.25em] max-lg:truncate max-lg:py-[0.25em]">
                              {resolveI18nText(item.title, item.i18n, language)}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div
                {...fadeUp(0.2)}
                className={`w-full flex-col lg:gap-[1.25rem] ${
                  disclaimer ? "flex" : "max-lg:hidden lg:flex"
                }`}
              >
                <div className="min-h-[8rem] items-start justify-between border-t border-[rgba(209,232,221,0.16)] pt-[2.75rem] font-geist text-[1.75rem] leading-[2.0625rem] text-[#879a92] max-lg:hidden lg:flex">
                  <p className="mb-0">{data.legalEntity}</p>
                  <p className="mb-0">
                    © {new Date().getFullYear()} IQ Capital.{" "}
                    {RIGHTS_RESERVED[language] ?? RIGHTS_RESERVED.en}
                  </p>
                </div>

                {disclaimer && (
                  <div className="flex flex-col max-lg:gap-[1.5rem] max-lg:border-t max-lg:border-white/20 max-lg:pt-[4rem]">
                    <p className="mb-0 font-geist text-[2rem] leading-[1.5] text-white/50 lg:hidden">
                      {DISCLAIMER_LABEL[language] ?? DISCLAIMER_LABEL.en}
                    </p>
                    <div className="richtext richtext-start w-full max-w-none gap-0 text-left font-geist [&_p]:mb-0 [&_strong]:text-inherit max-lg:text-[2rem] max-lg:leading-[1.5] max-lg:text-white/50 max-lg:[&_p]:text-[2rem] max-lg:[&_p]:leading-[1.5] max-lg:[&_p]:text-white/50 lg:text-[1.5rem] lg:leading-[2.325rem] lg:text-[#879a92] lg:[&_p]:text-[1.5rem] lg:[&_p]:leading-[2.325rem] lg:[&_p]:text-[#879a92]">
                      <PortableText
                        value={disclaimer}
                        components={richTextComponent}
                      />
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
}
